import { useEffect, useState } from 'react';
import notify from '../../Utils/notify';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import { es } from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import EditCitaModal from './EditCitaModal';
import NewCitaModal from './NewCitaModal';
import usePermisos from '../../hooks/usePermisos';
import NoPermissions from '../NoPermissions/NoPermissions';
import Loader from '../../Layout/Loaders/Loader';
import axios from '../../axios';
import { CalendarDaysIcon, PencilSquareIcon, TrashIcon, ArrowPathIcon, PaperAirplaneIcon, PlusIcon } from '@heroicons/react/24/outline';
import Table, { useTableSort, useTablePagination } from '../UI/Table';

const today = new Date();
const PERMISOS_REQUERIDOS = [
    'appointments_list_read',
    'appointments_update',
    'appointments_clean'
];

const Agenda = () => {
    const { loading: loadingPermisos, tienePermiso, tieneRol } = usePermisos(PERMISOS_REQUERIDOS);
    const [data, setData] = useState({ start: today, end: null, sucursal_id: null});
    const [showCalendar, setShowCalendar] = useState(false);
    const [citas, setCitas] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [selectedDay, setSelectedDay] = useState(today);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
    const [citaSeleccionada, setCitaSeleccionada] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
    const [diasFestivos, setDiasFestivos] = useState([]);
    const [mesVisualizado, setMesVisualizado] = useState(today); // Mes que se está viendo en el calendario
    const [endDateLimit, setEndDateLimit] = useState(undefined); // Fecha límite para seleccionar en el calendario

    // Modales
    const [editCitaModalOpen, setEditCitaModalOpen] = useState(false);
    const [newCitaModalOpen, setNewCitaModalOpen] = useState(false);
    const [newCitaHora, setNewCitaHora] = useState(null); // Ahora puede ser string u objeto {hora, duracionSlot}

    // Hooks para tabla reutilizable
    const { sortedData, sortConfig, handleSort } = useTableSort(citas, { field: 'hora', direction: 'ASC' });
    const { 
        paginatedData, 
        page, 
        setPage, 
        itemsPerPage, 
        setItemsPerPage, 
        totalItems,
        totalPages 
    } = useTablePagination(sortedData, { page: 1, itemsPerPage: 50 });

    useEffect(() => {
        console.log("Es admin:", tieneRol('admin'));
        // Establecer fecha límite para el calendario según el rol del usuario
        if (tieneRol('admin')) {
            setEndDateLimit(undefined); // Sin límite para admin
        } else {
            setEndDateLimit(new Date(new Date().setMonth(new Date().getMonth() + 2)));
        }
    }, [loadingPermisos, tieneRol]);

    useEffect(() => {
        const sucursal = localStorage.getItem('sucursal_selected');
        if (sucursal !== null) {
            setSucursalSeleccionada(sucursal);
        }
    }, []);

    useEffect(() => {
        axios.get('api/sucursals').then((resp) => {
            setSucursales(resp.data);
        }).catch(e => console.log("Error: ", e));
    }, []);

    useEffect(() => {
        if (selectedDay) {
            const pad = n => String(n).padStart(2, '0');
            let formatedDay = `${selectedDay.getFullYear()}-${pad(selectedDay.getMonth() + 1)}-${pad(selectedDay.getDate())}`;
            setData({ ...data, start: formatedDay, sucursal_id: sucursalSeleccionada });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, sucursalSeleccionada]);

    useEffect(() => {
        console.log("Data para obtener citas:", data);
        getCitas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Cargar días festivos del año completo al seleccionar sucursal
    useEffect(() => {
        if (!sucursalSeleccionada) {
            setDiasFestivos([]);
            return;
        }
        
        const añoActual = new Date().getFullYear();
        const primerDiaAño = new Date(añoActual, 0, 1);
        const ultimoDiaAño = new Date(añoActual, 11, 31);
        
        const formatDate = (date) => {
            const pad = n => String(n).padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
        };
        
        const fechaInicio = formatDate(primerDiaAño);
        const fechaFin = formatDate(ultimoDiaAño);
     
        axios.post('api/holidays/en-rango', {
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            sucursal_id: parseInt(sucursalSeleccionada)
        }).then(resp => {
            const fechas = resp.data.fechas || [];
            setDiasFestivos(fechas);
        }).catch(e => {
            console.error("Error cargando festivos:", e.response?.data || e.message);
            setDiasFestivos([]);
        });
    }, [sucursalSeleccionada]);

    // Auto-refresh cada 15 minutos con Page Visibility API
    useEffect(() => {
        if (!sucursalSeleccionada) return;

        const INTERVALO_REFRESH = 15 * 60 * 1000; // 15 minutos

        const actualizarSilenciosamente = () => {
            // No actualizar si hay modales abiertos (previene pérdida de datos)
            if (editCitaModalOpen) {
                return;
            }

            // No actualizar si la pestaña no está visible
            if (document.hidden) {
                return;
            }

            console.log('Auto-refresh: actualizando citas...');
            axios.post('api/agenda-slots', { sucursal_id: data.sucursal_id, fecha: data.start })
                .then((resp) => {
                    setCitas(resp.data);
                    setUltimaActualizacion(new Date());
                    notify.info('Agenda actualizada automáticamente', { duration: 2000 });
                })
                .catch(e => {
                    console.log("Error en auto-refresh: ", e);
                });
        };

        // Handler para Page Visibility API
        const handleVisibilityChange = () => {
            if (!document.hidden && sucursalSeleccionada) {
                actualizarSilenciosamente();
            }
        };

        // Configurar intervalo
        const intervalId = setInterval(actualizarSilenciosamente, INTERVALO_REFRESH);

        // Escuchar cambios de visibilidad
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Cleanup
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [sucursalSeleccionada, data, editCitaModalOpen]);

    const getCitas = () => {
        setLoading(true);
        axios.post('api/agenda-slots', { sucursal_id: data.sucursal_id, fecha: data.start }).then((resp) => {
            console.log("Slots obtenidos:", resp.data);
            setCitas(resp.data);
            setUltimaActualizacion(new Date());
            setLoading(false);
        }).catch(e => {
            console.log("Error: ", e);
            setLoading(false);
        });
    };

    const sincronizarCitas = () => {
        if (!sucursalSeleccionada) {
            notify.warning('Selecciona una sucursal antes de sincronizar');
            return;
        }
        setSincronizando(true);
        axios.post('api/agenda-slots', { sucursal_id: data.sucursal_id, fecha: data.start }).then((resp) => {
            setCitas(resp.data);
            setUltimaActualizacion(new Date());
            setSincronizando(false);
            notify.success('Citas sincronizadas correctamente');
        }).catch(e => {
            console.log("Error al sincronizar: ", e);
            setSincronizando(false);
            notify.error('Error al sincronizar las citas');
        });
    };

    const cleanCita = (citaId) => {
        if(!window.confirm("¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer.")) return;
        
        axios.delete(`api/appointments/${citaId}`).then(resp => {
            if (resp.status === 200 || resp.status === 204) {
                // Refrescar la lista de citas después de eliminar
                getCitas();
                notify.success('Cita eliminada correctamente');
            }
        }).catch(e => {
            console.log("Error al eliminar cita: ", e);
            notify.error('Error al eliminar la cita');
        });
    };


    const openNewCitaModal = (hora) => {
        // Calcular duración del slot actual basándose en la diferencia con el siguiente slot
        const slotActualIndex = citas.findIndex(c => c.hora === hora);
        let duracionSlot = 30; // Default: 30 minutos
        
        if (slotActualIndex !== -1 && slotActualIndex + 1 < citas.length) {
            const slotActual = citas[slotActualIndex];
            const siguienteSlot = citas[slotActualIndex + 1];
            
            // Calcular diferencia en minutos entre slots
            const [horaActual, minActual] = slotActual.hora.split(':').map(Number);
            const [horaSiguiente, minSiguiente] = siguienteSlot.hora.split(':').map(Number);
            
            const minutosActual = horaActual * 60 + minActual;
            const minutosSiguiente = horaSiguiente * 60 + minSiguiente;
            
            duracionSlot = minutosSiguiente - minutosActual;
        }
        
        // Guardamos la hora y duración calculada para pasarla al modal
        setNewCitaHora({ hora, duracionSlot });
        setNewCitaModalOpen(true);
    };

    const resendNotifications = async (citaId) => {
        const cita = citas.find(c => c.id === citaId);
        
        if (!cita || !cita.nombre_paciente) {
            notify.warning('Esta cita no tiene un paciente asignado');
            return;
        }

        const confirmMsg = `¿Deseas reenviar las notificaciones para esta cita?\n\n` +
            `Paciente: ${cita.nombre_paciente}\n` +
            `Teléfono: ${cita.telefono_paciente || 'N/A'}\n` +
            `Fecha: ${cita.fecha} ${cita.hora}`;
        
        if (!window.confirm(confirmMsg)) {
            return;
        }

        try {
            const resp = await axios.post(`api/appointments/${citaId}/resend-notifications`);
            
            if (resp.status === 200 && resp.data.success) {
                const emailSent = resp.data.notifications?.email?.sent || false;
                const whatsappSent = resp.data.notifications?.whatsapp?.sent || false;
                
                if (emailSent && whatsappSent) {
                    notify.success('Notificaciones enviadas correctamente por email y WhatsApp');
                } else if (emailSent) {
                    notify.success('Notificación enviada correctamente por email');
                } else if (whatsappSent) {
                    notify.success('Notificación enviada correctamente por WhatsApp');
                } else {
                    notify.warning('No se pudo enviar ninguna notificación. Verifica que la cita tenga email o teléfono.');
                }
            }
        } catch (e) {
            console.log('Error al reenviar notificaciones:', e);
            const errorMsg = e.response?.data?.message || 'Error al reenviar las notificaciones';
            notify.error(errorMsg);
        }
    };

    const openEditModal = async (citaId) => {
        // Obtener versión fresca de la cita antes de abrir el modal
        try {
            const resp = await axios.get(`api/appointments/${citaId}`);
            const citaFresca = resp.data;
            
            // Verificar si la cita ya está ocupada (tiene paciente y productos)
            if (citaFresca.nombre_paciente && citaFresca.products?.length > 0) {
                const source = citaFresca.source || 'Desconocido';
                const confirmMsg = `⚠️ ADVERTENCIA: Esta cita ya está ocupada\n\n` +
                    `Paciente: ${citaFresca.nombre_paciente}\n` +
                    `Teléfono: ${citaFresca.telefono_paciente || 'N/A'}\n` +
                    `Origen: ${source}\n` +
                    `Estudios: ${citaFresca.products.length}\n\n` +
                    `Editarla podría causar conflictos con otra reserva.\n` +
                    `¿Deseas continuar de todos modos?`;
                
                if (!window.confirm(confirmMsg)) {
                    notify.info('Edición cancelada');
                    return;
                }
            }
            
            setCitaSeleccionada(citaId);
            setEditCitaModalOpen(true);
        } catch (e) {
            console.log('Error al cargar la cita:', e);
            notify.error('Error al cargar la cita. Intenta refrescar la lista.');
        }
    };

    return (
        <div>
            <div className="container-admin-menu">
                <Breadcrumb hasButton={false} hasFilters={false} />
            </div>
            <div className="container-admin-container">
                <div className="container-admin bg-white rounded-lg shadow-md p-4">
                    {/* Modales */}
                    <EditCitaModal
                        open={editCitaModalOpen}
                        onClose={() => {
                            setEditCitaModalOpen(false);
                            setCitaSeleccionada(null);
                            getCitas();
                        }}
                        citaId={citaSeleccionada}
                        citas={citas}
                        setCitas={setCitas}
                        appointments_clean={tienePermiso('appointments_clean')}
                    />
                    <NewCitaModal
                        open={newCitaModalOpen}
                        onClose={() => {
                            setNewCitaModalOpen(false);
                            setNewCitaHora(null);
                        }}
                        sucursalId={data.sucursal_id}
                        fecha={data.start}
                        hora={typeof newCitaHora === 'object' ? newCitaHora?.hora : newCitaHora}
                        slotDuracionMinutos={typeof newCitaHora === 'object' ? newCitaHora?.duracionSlot : 30}
                        onCreated={() => getCitas()}
                    />

                    {/* Header con filtros */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 info-badge rounded-xl p-4 shadow-sm mb-6">
                        <div className="flex-1">
                            <div className="flex flex-col md:flex-row md:items-center gap-4">
                                <div>
                                    <h6 className="text-lg font-semibold text-white">
                                        <span className="font-bold">Fecha:</span> {selectedDay.toLocaleDateString("es-MX", { year: 'numeric', month: 'long', day: 'numeric' })}
                                    </h6>
                                </div>
                                <div className="w-full md:w-64">
                                    <select
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 browser-default text-gray-600"
                                        value={sucursalSeleccionada}
                                        onChange={(e) => setSucursalSeleccionada(e.target.value)}
                                    >
                                        <option value="" disabled>Seleccione la sucursal</option>
                                        {sucursales.map((sucursal) => (
                                            <option value={sucursal.id} key={sucursal.id}>{sucursal.nombre_sucursal}</option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                        setSelectedDay(today);
                                        setMesVisualizado(today); // Sincronizar el mes visualizado
                                    }}
                                >
                                    <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                                    Citas de hoy
                                </button>
                                <button
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={sincronizarCitas}
                                    disabled={sincronizando || !sucursalSeleccionada}
                                    title={!sucursalSeleccionada ? "Seleccione una sucursal primero" : "Sincronizar citas"}
                                >
                                    <ArrowPathIcon className={`h-5 w-5 ${sincronizando ? 'animate-spin' : ''}`} />
                                    {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
                                </button>
                                {diasFestivos.length > 0 && (
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
                                        <span className="font-semibold">{diasFestivos.length}</span>
                                        <span>día{diasFestivos.length > 1 ? 's' : ''} festivo{diasFestivos.length > 1 ? 's' : ''} en {new Date().getFullYear()}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="relative">
                            {showCalendar ? (
                                <div
                                    className="absolute right-0 top-0 z-20 bg-white rounded-lg shadow-lg border"
                                    onMouseLeave={() => setShowCalendar(false)}
                                >
                                    <DayPicker
                                        mode="single"
                                        locale={es}
                                        selected={selectedDay}
                                        month={mesVisualizado}
                                        onMonthChange={setMesVisualizado}
                                        fromDate={new Date()}
                                        toDate={endDateLimit}
                                        onSelect={(day) => {
                                            if(!day) return;
                                            
                                            // Validar que no sea día festivo
                                            const year = day.getFullYear();
                                            const month = String(day.getMonth() + 1).padStart(2, '0');
                                            const dayNum = String(day.getDate()).padStart(2, '0');
                                            const dateStr = `${year}-${month}-${dayNum}`;
                                            
                                            if (diasFestivos.includes(dateStr)) {
                                                notify.warning('No se puede seleccionar un día festivo');
                                                return;
                                            }
                                            
                                            setSelectedDay(day);
                                            setMesVisualizado(day); // Sincronizar el mes visualizado
                                            setShowCalendar(false);
                                        }}
                                        disabled={(date) => {
                                            const year = date.getFullYear();
                                            const month = String(date.getMonth() + 1).padStart(2, '0');
                                            const day = String(date.getDate()).padStart(2, '0');
                                            const dateStr = `${year}-${month}-${day}`;
                                            const isFestivo = diasFestivos.includes(dateStr);
                                            if (isFestivo) {
                                                console.log("Fecha festiva bloqueada:", dateStr);
                                            }
                                            return isFestivo;
                                        }}
                                        modifiers={{
                                            festivo: diasFestivos.map(fechaStr => {
                                                const [year, month, day] = fechaStr.split('-');
                                                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                            })
                                        }}
                                        modifiersClassNames={{
                                            festivo: 'bg-red-100 text-red-600 line-through'
                                        }}
                                        className="p-2 text-gray-500"
                                    />
                                </div>
                            ) : (
                                
                                <button
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => {
                                        setMesVisualizado(selectedDay); // Sincronizar con el día seleccionado al abrir
                                        setShowCalendar(true);
                                    }}
                                >
                                   <img className='cursor-pointer w-5 h-5' src="/images/calendar2.png" alt="Agregar días"/>
                                    Ver Calendario
                                </button> 

                            )}
                        </div>
                    </div>

                    {/* Contenido principal */}
                    {loadingPermisos ? (
                        <Loader />
                    ) : tienePermiso('appointments_list_read') ? (
                        <Table
                            columns={[
                                {
                                    key: 'hora',
                                    label: 'Hora',
                                    sortable: true,
                                    render: (value, row) => (
                                        <span className="whitespace-nowrap font-semibold text-blue-700">{value}</span>
                                    )
                                },
                                {
                                    key: '_type',
                                    label: 'Estado',
                                    render: (value, row) => value === 'occupied'
                                        ?   (row.status === 'cancelada'
                                                ? <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full font-medium">Cancelada</span>
                                                : <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">Ocupado</span>
                                          )

                                        : <span className="inline-block bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">Disponible</span>
                                },
                                {
                                    key: 'nombre_paciente',
                                    label: 'Paciente',
                                    sortable: true,
                                    render: (value) => (
                                        <span className="whitespace-nowrap">{value || '-'}</span>
                                    )
                                },
                                {
                                    key: 'telefono_paciente',
                                    label: 'Teléfono',
                                    render: (value) => (
                                        <span className="whitespace-nowrap">{value || '-'}</span>
                                    )
                                },
                                {
                                    key: 'products',
                                    label: 'Estudios',
                                    render: (products) => (
                                        <div className="flex flex-col gap-1">
                                            {products?.length > 0 ? products.map((p, i) => (
                                                <span
                                                    key={p.id ?? i}
                                                    className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded"
                                                >
                                                    {p.nombre_producto} - {p.duracion_aproximada} min
                                                </span>
                                            )) : '-'}
                                        </div>
                                    )
                                },
                                {
                                    key: 'doctor',
                                    label: 'Doctor',
                                    sortable: true,
                                    render: (value) => (
                                        <span className="whitespace-nowrap">{value || '-'}</span>
                                    )
                                },
                                {
                                    key: 'observaciones',
                                    label: 'Observaciones',
                                    sortable: true,
                                    render: (value) => (
                                        <div className="w-64 p-4" >
                                            <span className="wrap-normal whitespace-normal">{value || '-'}</span> 
                                        </div>
                                    )
                                },
                                {
                                    key: 'excedido',
                                    label: 'Excedido',
                                    sortable: true,
                                    render: (value, row) => {
                                        if (row._type === 'empty') return '-';
                                        const excedido = Number(value) || 0;
                                        if (excedido === 0) {
                                            return <span className="text-green-700 font-medium">✓ A tiempo</span>;
                                        }
                                        return (
                                            <span className="text-orange-700 font-semibold">
                                                +{excedido} min
                                            </span>
                                        );
                                    }
                                },
                                {
                                    key: 'source',
                                    label: 'Origen',
                                    sortable: true,
                                    render: (value) => <span className="whitespace-nowrap">{value || '-'}</span>
                                }
                            ]}
                            data={paginatedData}
                            actions={[
                                {
                                    type: 'custom',
                                    tooltip: 'Crear cita en este horario',
                                    icon: PlusIcon,
                                    permission: 'appointments_update',
                                    condition: (row) => row._type === 'empty',
                                    onClick: (row) => openNewCitaModal(row.hora),
                                    className: 'text-blue-600 hover:text-blue-900'
                                },
                                {
                                    type: 'edit',
                                    tooltip: 'Editar cita',
                                    icon: PencilSquareIcon,
                                    permission: 'appointments_update',
                                    condition: (row) => row._type === 'occupied',
                                    onClick: (row) => openEditModal(row.id)
                                },
                                {
                                    type: 'custom',
                                    tooltip: 'Reenviar notificaciones',
                                    icon: PaperAirplaneIcon,
                                    permission: 'appointments_update',
                                    condition: (row) => row._type === 'occupied',
                                    onClick: (row) => resendNotifications(row.id),
                                    className: 'text-green-600 hover:text-green-900'
                                },
                                {
                                    type: 'delete',
                                    tooltip: 'Eliminar cita',
                                    icon: TrashIcon,
                                    condition: (row) => row._type === 'occupied',
                                    onClick: (row) => cleanCita(row.id),
                                    permission: 'appointments_update'
                                }
                            ]}
                            permissions={{
                                appointments_update: tienePermiso('appointments_update')
                            }}
                            sorting={{
                                field: sortConfig.field,
                                direction: sortConfig.direction,
                                onSort: handleSort
                            }}
                            pagination={{
                                page,
                                itemsPerPage,
                                totalItems,
                                onPageChange: setPage,
                                onItemsPerPageChange: setItemsPerPage,
                                itemsPerPageOptions: [10, 25, 50, 100]
                            }}
                            keyExtractor={(row) => row._slot_key}
                            loading={loading}
                            striped={true}
                            hoverable={true}
                            shadow={true}
                            rounded={true}
                            rowClassName={(row) => {
                                if (row._type === 'empty') return '';
                                
                                // Citas canceladas en rojo
                                if (row.status === 'cancelada') {
                                    return 'bg-red-50 hover:bg-red-100 transition-colors';
                                }
                                
                                const excedido = Number(row.excedido) || 0;
                                if (excedido > 0) {
                                    return 'bg-orange-50 hover:bg-orange-100 transition-colors';
                                }
                                return 'bg-green-50 hover:bg-green-100 transition-colors';
                            }}
                            emptyMessage="No hay slots configurados para esta sucursal y día"
                        />
                    ) : (
                        <NoPermissions />
                    )}
                </div>
            </div>
        </div>
    );
};

export default Agenda;